import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["agentType", "betaDetails", "installUrl", "onClick", "onEmbedClose", "onInstallComplete", "onInstallError", "slug", "sourceId"];
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { useAiAccessControls } from 'ai-studio-lib/shared/hooks/queries/useAiAccessControls';
import { getAiSettingsUrl } from 'ai-studio-lib/shared/utils/urls';
import { useSendOnCopilotChannel } from 'copilot-toolkit/channel/hooks/useSendOnCopilotChannel';
import { Button } from 'foundations-components/transitional/components/Button';
import { ButtonWrapper } from 'foundations-components/transitional/components/ButtonWrapper';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { Link } from 'foundations-components/transitional/components/Link';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import { LoadingButton } from 'foundations-components/transitional/components/LoadingButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { gate } from 'hub-http/gates';
import { useCallback, useMemo, useState } from 'react';
import ToolAccessTooltip from 'ui-tool-access/ToolAccessTooltip';
import { useCreateInstanceFromTemplateByGlobalIdMutation, useGetInstancesByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/ai/agent/rpc/AiAgentInstancesChirpServiceV2.hooks';
import { useGetCustomAssistantQuery, useInstallCustomAssistantTemplateFromGlobalIdMutation } from '../../__generated__/chirp/com/hubspot/customassistants/rpc/CustomAssistantsService.hooks';
import { useListTemplateInstallsByGlobalIdQuery } from '../../__generated__/chirp/com/hubspot/customassistants/rpc/TemplateManagementAppService.hooks';
import { useSendInstallIntentLazyQuery } from '../../__generated__/chirp/com/hubspot/marketplace/installs/rpc/MarketplaceInstallsRpc-app.hooks';
import { useGetSatisfyingOptionsQuery } from '../../__generated__/chirp/com/hubspot/scopegaps/rpc/ScopeGapsService.hooks';
import { useGetActiveToolAccessRequestForScopeQuery } from '../../__generated__/chirp/com/hubspot/tool/access/rpc/ToolAccessService.hooks';
import { useDetailsPageContext } from '../context/DetailsPageContext';
import { useInstallability } from '../hooks/useInstallability';
import { JoinPublicBetaModal } from '../install/JoinPublicBetaModal';
import { getAgentStudioUrl, getBreezeStudioUrl, getFormattedShortlinkUrl } from '../utils/DetailsPageUrls';
import { trackCustomMarketplaceOfferingBehavioralEvent } from '../utils/TrackingUtils';
import { getAiAccessControlsTooltip } from '../utils/aiAccessControlsUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const BREEZE_AGENT_EDIT = 'breeze-agent-edit';
const BREEZE_AGENT_ACCESS = 'breeze-agent-access';
function BreezeStudioLink({
  agentType,
  children
}) {
  return /*#__PURE__*/_jsx(Link, {
    external: true,
    href: getBreezeStudioUrl(agentType),
    children: children
  });
}
export function AgentCTAButton(_ref) {
  var _installabilityData$i10, _uninstallableReason$;
  let {
      agentType,
      betaDetails,
      installUrl,
      onClick,
      onEmbedClose,
      onInstallComplete,
      onInstallError,
      slug,
      sourceId
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const {
    appContext,
    listingId,
    name: listingName,
    published,
    productType,
    isEmbedded,
    trackInteraction,
    trackInstall,
    userInfo
  } = useDetailsPageContext();
  const [sendInstallIntent] = useSendInstallIntentLazyQuery();
  const {
    accessControls
  } = useAiAccessControls();
  const isPublicBeta = (betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.betaStage) === 'PUBLIC_BETA';
  const [isPublicBetaModalOpen, setIsPublicBetaModalOpen] = useState(false);
  const hasJoinedPublicBeta = useMemo(() => {
    if (isPublicBeta && betaDetails !== null && betaDetails !== void 0 && betaDetails.gateName) {
      var _userInfo$gates;
      // eslint-disable-next-line hubspot-dev/no-bare-gate-strings
      return userInfo === null || userInfo === void 0 || (_userInfo$gates = userInfo.gates) === null || _userInfo$gates === void 0 ? void 0 : _userInfo$gates.includes(gate(betaDetails.gateName));
    }
    return true;
  }, [isPublicBeta, betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.gateName, userInfo === null || userInfo === void 0 ? void 0 : userInfo.gates]);
  const offPlatformInstallUrl = installUrl ? getFormattedShortlinkUrl(installUrl) : installUrl;
  const {
    data: installabilityData,
    loading: installabilityLoading
  } = useInstallability({
    listingId,
    published,
    appContext
  });
  const {
    data: breezeAgentEditAccessRequestData,
    refetch: refetchBreezeAgentEditAccessRequest
  } = useGetActiveToolAccessRequestForScopeQuery({
    variables: {
      scopeName: 'breeze-agent-edit'
    },
    skip: appContext === 'admin-preview'
  });
  const onToolAccessRequestComplete = useCallback(() => {
    trackInteraction({
      action: 'breeze agent edit access request sent'
    });
    void refetchBreezeAgentEditAccessRequest().catch(error => {
      console.error('Error refetching breeze agent edit access request status:', error);
    });
  }, [refetchBreezeAgentEditAccessRequest, trackInteraction]);
  const launchCopilotWithCustomCopilot = useSendOnCopilotChannel('LAUNCH_WITH_CUSTOM_COPILOT');
  const {
    installed,
    missingScopes,
    userInstallable,
    portalInstallable
  } = useMemo(() => {
    var _installabilityData$i, _installabilityData$i2, _installabilityData$i3, _installabilityData$i4, _installabilityData$i5, _installabilityData$i6, _installabilityData$i7, _installabilityData$i8, _installabilityData$i9;
    const missingUserScopes = (_installabilityData$i = installabilityData === null || installabilityData === void 0 || (_installabilityData$i2 = installabilityData.installability) === null || _installabilityData$i2 === void 0 || (_installabilityData$i2 = _installabilityData$i2.uninstallableReason) === null || _installabilityData$i2 === void 0 ? void 0 : _installabilityData$i2.missingUserScopes) !== null && _installabilityData$i !== void 0 ? _installabilityData$i : [];
    const missingPortalScopes = (_installabilityData$i3 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i4 = installabilityData.installability) === null || _installabilityData$i4 === void 0 || (_installabilityData$i4 = _installabilityData$i4.uninstallableReason) === null || _installabilityData$i4 === void 0 ? void 0 : _installabilityData$i4.missingPortalScopes) !== null && _installabilityData$i3 !== void 0 ? _installabilityData$i3 : [];
    const allMissingScopes = [...missingUserScopes.map(scope => scope.toLowerCase().replaceAll('_', '-')), ...missingPortalScopes.map(scope => scope.toLowerCase().replaceAll('_', '-'))];
    return {
      installed: (_installabilityData$i5 = installabilityData === null || installabilityData === void 0 ? void 0 : installabilityData.installed) !== null && _installabilityData$i5 !== void 0 ? _installabilityData$i5 : false,
      missingScopes: allMissingScopes,
      userInstallable: (_installabilityData$i6 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i7 = installabilityData.installability) === null || _installabilityData$i7 === void 0 ? void 0 : _installabilityData$i7.userInstallable) !== null && _installabilityData$i6 !== void 0 ? _installabilityData$i6 : true,
      //TODO: fallback to false once agent instance reliability is improved
      portalInstallable: (_installabilityData$i8 = installabilityData === null || installabilityData === void 0 || (_installabilityData$i9 = installabilityData.installability) === null || _installabilityData$i9 === void 0 ? void 0 : _installabilityData$i9.portalInstallable) !== null && _installabilityData$i8 !== void 0 ? _installabilityData$i8 : true //TODO: fallback to false once agent instance reliability is improved
    };
  }, [installabilityData]);
  const {
    data: missingPlanData
  } = useGetSatisfyingOptionsQuery({
    variables: {
      scopes: missingScopes
    },
    skip: missingScopes.length === 0
  });
  const uninstallableReason = installabilityData === null || installabilityData === void 0 || (_installabilityData$i10 = installabilityData.installability) === null || _installabilityData$i10 === void 0 ? void 0 : _installabilityData$i10.uninstallableReason;
  const missingBreezeAgentAccess = uninstallableReason === null || uninstallableReason === void 0 ? void 0 : uninstallableReason.missingUserScopes.includes(BREEZE_AGENT_ACCESS);
  const OpenAISettingsLink = useMemo(() => ({
    children
  }) => {
    return /*#__PURE__*/_jsx(Link, {
      external: true,
      href: getAiSettingsUrl(),
      onClick: () => {
        trackInteraction({
          action: 'click ai settings link'
        });
      },
      use: "on-dark",
      whiteSpace: "nowrap",
      children: children
    });
  }, [trackInteraction]);

  // Check if user has required access controls
  const aiAccessControlsTooltip = useMemo(() => getAiAccessControlsTooltip(accessControls, agentType, OpenAISettingsLink, sourceId), [sourceId, accessControls, agentType, OpenAISettingsLink]);

  // Prepare tooltip content for disabled button
  const tooltipContent = useMemo(() => {
    const missingPlanOptions = missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions;
    if (missingPlanOptions && missingPlanOptions.length === 1 && missingPlanOptions[0].and.length === 1) {
      if (missingPlanOptions[0].and[0].or.length === 1) {
        const option = missingPlanOptions[0].and[0].or[0];
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlan",
          options: {
            planName: option.displayName,
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        });
      }
      return /*#__PURE__*/_jsxs("span", {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.missingPlans",
          options: {
            productType: unescapedText(`detailsPage.productType.${productType}`)
          }
        }), /*#__PURE__*/_jsx(List, {
          marginBlockStart: "200",
          marginInlineStart: "-400",
          styled: true,
          children: missingPlanOptions[0].and[0].or.map((option, index) => /*#__PURE__*/_jsx(ListItem, {
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.productDetailsCard.cta.uninstallableReasons.orHigherTier",
              options: {
                planName: option.displayName
              }
            })
          }, index))
        })]
      });
    }

    // Check for AI access controls
    if (aiAccessControlsTooltip) {
      return aiAccessControlsTooltip;
    }

    // Default to cannot install generic text, should not reach here often
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.uninstallableReasons.cannotInstallAgent"
    });
  }, [aiAccessControlsTooltip, missingPlanData === null || missingPlanData === void 0 ? void 0 : missingPlanData.satisfyingOptions, productType]);
  const {
    data: agentInstancesData,
    loading: agentInstanceLoading,
    refetch: agentInstancesRefetch
  } = useGetInstancesByGlobalIdQuery({
    skip: !sourceId || appContext !== 'app' || agentType !== 'STUDIO',
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const agentInstance = useMemo(() => {
    if (!agentInstancesData || agentInstancesData.instances.length === 0) {
      return undefined;
    }
    return agentInstancesData.instances[0];
  }, [agentInstancesData]);

  /**
   * Determines the href for the agent CTA button based on agent type and instance data
   */
  const getAgentOpenButtonHref = useCallback(() => {
    switch (agentType) {
      case 'OFF_PLATFORM':
        return offPlatformInstallUrl;
      case 'STUDIO':
        return agentInstance !== null && agentInstance !== void 0 && agentInstance.id ? getAgentStudioUrl(agentInstance.id) : getBreezeStudioUrl(agentType);
      case 'ASSISTANT':
        return undefined;
      // No href, handled by onClick
      default:
        return undefined;
    }
  }, [agentType, offPlatformInstallUrl, agentInstance === null || agentInstance === void 0 ? void 0 : agentInstance.id]);
  const {
    data: installedAssistantData,
    refetch: installedAssistantRefetch,
    loading: installedAssistantLoading
  } = useListTemplateInstallsByGlobalIdQuery({
    variables: {
      templateGlobalId: sourceId || '',
      offset: {
        offset: 0
      },
      // CHIRP pagination format
      pageLength: 10 // only need to check if at least one install exists
    },
    skip: !sourceId || appContext !== 'app' || agentType !== 'ASSISTANT'
  });
  const installedAssistant = useMemo(() => {
    if (!installedAssistantData || !installedAssistantData.results) {
      return undefined;
    }

    // If there's at least one install, return the first one
    return installedAssistantData.results.length > 0 ? installedAssistantData.results[0] : undefined;
  }, [installedAssistantData]);

  // Fetch the custom assistant details if we have an installed assistant
  const {
    data: customAssistantDetails
  } = useGetCustomAssistantQuery({
    variables: {
      id: (installedAssistant === null || installedAssistant === void 0 ? void 0 : installedAssistant.customAssistantId) || 0
    },
    skip: !(installedAssistant !== null && installedAssistant !== void 0 && installedAssistant.customAssistantId)
  });
  const [createAgentInstance, {
    loading: createAgentInstanceLoading
  }] = useCreateInstanceFromTemplateByGlobalIdMutation({
    variables: {
      globalId: sourceId ? parseInt(sourceId, 10) : 0,
      globalIdString: sourceId !== null && sourceId !== void 0 ? sourceId : ''
    }
  });
  const [installAssistantFromGlobalId, {
    loading: installAssistantFromGlobalIdLoading
  }] = useInstallCustomAssistantTemplateFromGlobalIdMutation({
    variables: {
      templateGlobalId: sourceId || ''
    }
  });
  const handleAgentInstalledSuccessfully = useCallback(instanceId => {
    if (agentType === 'ASSISTANT') {
      // Refetch assistant install data after successful install
      installedAssistantRefetch().catch(error => {
        console.error(error);
      });
    }
    FloatingAlertStore.addAlert({
      maxInlineSize: 'unset',
      sticky: true,
      titleText: isPublicBetaModalOpen ?
      /*#__PURE__*/
      //show specific message if beta joined as part of install
      _jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentBetaJoinedAndInstalledSuccessAlert",
        options: {
          listingName
        }
      }) : listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledSuccessAlertTitle_jsx",
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledSuccessAlertTitle"
      }),
      type: 'success',
      message: instanceId && /*#__PURE__*/_jsx(ButtonWrapper, {
        children: /*#__PURE__*/_jsx(Button, {
          href: getAgentStudioUrl(instanceId),
          onClick: () => trackInteraction({
            action: 'clicked open cta button',
            subscreen: 'install success alert'
          }),
          external: true,
          size: "small",
          use: "tertiary",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.productDetailsCard.cta.open"
          })
        })
      })
    });
    agentInstancesRefetch().catch(error => {
      console.error('Error refetching agent data:', error);
    });
    if (onInstallComplete) {
      onInstallComplete();
    }
  }, [agentInstancesRefetch, agentType, installedAssistantRefetch, isPublicBetaModalOpen, listingName, onInstallComplete, trackInteraction]);
  const handleAgentInstallFailed = useCallback(error => {
    console.error('Error installing agent:', error);
    FloatingAlertStore.addAlert({
      titleText: isPublicBetaModalOpen ?
      /*#__PURE__*/
      //show specific message if beta joined but install failed
      _jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentBetaJoinedAndInstalledFailedAlert",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          listingName
        }
      }) : listingName ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.agentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        },
        options: {
          listingName
        }
      }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "detailsPage.productDetailsCard.alerts.genericAgentInstalledFailedAlertTitle_jsx",
        elements: {
          Link: BreezeStudioLink
        }
      }),
      type: 'danger'
    });
    if (onInstallError) {
      onInstallError();
    }
  }, [isPublicBetaModalOpen, listingName, onInstallError]);
  const handleInstallAgent = () => {
    if (agentType === 'ASSISTANT') {
      installAssistantFromGlobalId().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result;
          handleAgentInstalledSuccessfully((_response$data$result = response.data.result) === null || _response$data$result === void 0 || (_response$data$result = _response$data$result.customAssistant) === null || _response$data$result === void 0 ? void 0 : _response$data$result.id);
        }
      }).catch(error => handleAgentInstallFailed(error));
    } else {
      createAgentInstance().then(response => {
        if (response !== null && response !== void 0 && response.data) {
          var _response$data$result2;
          handleAgentInstalledSuccessfully((_response$data$result2 = response.data.result) === null || _response$data$result2 === void 0 || (_response$data$result2 = _response$data$result2.agentInstance) === null || _response$data$result2 === void 0 ? void 0 : _response$data$result2.id);
        }
      }).catch(error => {
        handleAgentInstallFailed(error);
      });
    }
  };
  const handlePostBetaJoin = () => {
    if (agentType === 'OFF_PLATFORM' && offPlatformInstallUrl) {
      window.open(offPlatformInstallUrl, '_blank', 'noopener');
    } else {
      handleInstallAgent();
    }
  };

  // Render a loading state button to prevent vulnerabilities that can be caused by assuming that a user can install
  if (createAgentInstanceLoading || installedAssistantLoading || installabilityLoading || agentInstanceLoading || installAssistantFromGlobalIdLoading) {
    return /*#__PURE__*/_jsx(LoadingButton, {
      size: "medium",
      use: "primary",
      disabled: true,
      loading: true,
      role: "button",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    });
  }

  // Render an "Open" button if the agent is installed except for when the user does not have agent studio access
  if ((installed || agentInstance !== null && agentInstance !== void 0 && agentInstance.id || installedAssistant) && !missingBreezeAgentAccess) {
    return /*#__PURE__*/_jsx(Button, Object.assign({
      use: "primary",
      href: getAgentOpenButtonHref(),
      external: isEmbedded,
      onClick: () => {
        onClick === null || onClick === void 0 || onClick();
        trackInteraction({
          action: 'clicked open cta button'
        });
        if (agentType === 'ASSISTANT' && installedAssistant && customAssistantDetails !== null && customAssistantDetails !== void 0 && customAssistantDetails.customAssistant) {
          launchCopilotWithCustomCopilot({
            customAssistantType: 'chatAgent',
            customAssistant: {
              id: customAssistantDetails.customAssistant.id,
              name: customAssistantDetails.customAssistant.name || listingName || '',
              welcomeMessage: customAssistantDetails.customAssistant.description,
              iconUrl: customAssistantDetails.customAssistant.icon,
              type: 'chatAgent'
            }
          });
          if (onEmbedClose) {
            onEmbedClose();
          }
        }
      }
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.open"
      })
    }));
  }

  // Render agent tool access request tooltip if user lacks edit/add access
  // Only show this if the portal CAN install (has required plan) but user lacks permission
  if (portalInstallable && uninstallableReason !== null && uninstallableReason !== void 0 && (_uninstallableReason$ = uninstallableReason.missingUserScopes) !== null && _uninstallableReason$ !== void 0 && _uninstallableReason$.includes(BREEZE_AGENT_EDIT)) {
    return /*#__PURE__*/_jsx(ToolAccessTooltip, {
      scope: "breeze-agent-edit",
      onComplete: onToolAccessRequestComplete,
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        disabled: true,
        "data-test-id": "disabled-install-button-with-tool-access-tooltip"
      }, rest, {
        children: (breezeAgentEditAccessRequestData === null || breezeAgentEditAccessRequestData === void 0 ? void 0 : breezeAgentEditAccessRequestData.status) === 'ACTIVE' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.uninstallableReasons.agentEditAccessPending"
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.AGENT"
        })
      }))
    });
  }

  // Render a disabled button if the agent is not installable
  if (!userInstallable || !portalInstallable || aiAccessControlsTooltip !== null) {
    return /*#__PURE__*/_jsx(Tooltip, {
      title: tooltipContent,
      minInlineSize: "240px",
      children: /*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        disabled: true,
        "data-test-id": "disabled-install-button"
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.productDetailsCard.cta.install.AGENT"
        })
      }))
    });
  }

  // Render a "Join beta and add" button if the agent is installable but the user has not joined the public beta
  if (isPublicBeta && !hasJoinedPublicBeta) {
    var _betaDetails$rolloutI;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Button, Object.assign({
        use: "primary",
        onClick: () => {
          onClick === null || onClick === void 0 || onClick();
          void sendInstallIntent({
            variables: {
              slug
            }
          });
          trackInteraction({
            action: 'clicked join beta and add cta button'
          });
          setIsPublicBetaModalOpen(true);
        }
      }, rest, {
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `detailsPage.productDetailsCard.cta.joinBetaAndAdd.${agentType === 'ASSISTANT' ? 'ASSISTANT' : 'AGENT'}`
        })
      })), isPublicBetaModalOpen && /*#__PURE__*/_jsx(JoinPublicBetaModal, {
        agentType: agentType || '',
        title: listingName,
        onClose: () => setIsPublicBetaModalOpen(false),
        rolloutId: (_betaDetails$rolloutI = betaDetails === null || betaDetails === void 0 ? void 0 : betaDetails.rolloutId) !== null && _betaDetails$rolloutI !== void 0 ? _betaDetails$rolloutI : 0,
        onSuccessfulJoin: handlePostBetaJoin,
        skipBetaAlert: agentType !== 'OFF_PLATFORM'
      })]
    });
  }

  // Render an external link button for off-platform agents
  if (agentType === 'OFF_PLATFORM') {
    return /*#__PURE__*/_jsx(Button, Object.assign({
      href: offPlatformInstallUrl,
      external: isEmbedded,
      onClick: () => {
        onClick === null || onClick === void 0 || onClick();
        void sendInstallIntent({
          variables: {
            slug
          }
        });
        trackInteraction({
          action: 'clicked off platform Install button'
        });
        trackInstall({});
      }
    }, rest, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.productDetailsCard.cta.install.AGENT"
      })
    }));
  }

  // Render standard button for all other agents
  return /*#__PURE__*/_jsx(Button, Object.assign({
    use: "primary",
    onClick: () => {
      onClick === null || onClick === void 0 || onClick();
      void sendInstallIntent({
        variables: {
          slug
        }
      });
      trackInstall({});
      trackCustomMarketplaceOfferingBehavioralEvent('HEADER_CONNECT');
      handleInstallAgent();
    }
  }, rest, {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.productDetailsCard.cta.install.AGENT"
    })
  }));
}